import { useTranslation } from "@/common/providers/i18n-provider";
import { StartFreeLink } from "@/components/cta-buttons";
import { appNameKey } from "@/config/compare";
import {
  COMPARE_PRICE_PARAMS,
  formatCompareOfferSummary,
  type OfferTranslate,
} from "@/lib/compare-offers";
import { ComparePrimaryActions } from "./primary-action-buttons";
import type { CompareRoundupConfig, RoundupApp } from "./roundup-types";

function rankingPrice(
  app: RoundupApp,
  keyPrefix: string,
  locale: string,
  t: OfferTranslate,
  offersAsOf: string,
): string {
  if (app.offerId) {
    return formatCompareOfferSummary(app.offerId, locale, t, offersAsOf);
  }
  return t(`${keyPrefix}.apps.${app.key}.price`, COMPARE_PRICE_PARAMS);
}

/**
 * Config order is the published rank; trade-offs are equally readable for every app.
 *
 * `offersAsOf` (`YYYY-MM-DD`) comes from the roundup loader — never the clock —
 * so server and browser decide intro expiry identically.
 *
 * Free-text keys resolve with `COMPARE_PRICE_PARAMS`, so they may quote our own
 * or a competitor's regular price as a parameter. The tagline is the exception:
 * it is also the ItemList description the loader resolves without parameters,
 * so it must stay price-free.
 */
export function RankingSection({
  config,
  offersAsOf,
}: {
  config: CompareRoundupConfig;
  offersAsOf: string;
}) {
  const { t, locale } = useTranslation();
  const kp = config.keyPrefix;
  return (
    <section className="compare-section">
      <div className="company-container">
        <div className="compare-heading">
          <h2>{t(`${kp}.ranking.title`)}</h2>
          <p>{t(`${kp}.ranking.subtitle`, COMPARE_PRICE_PARAMS)}</p>
        </div>
        <ol className="compare-rankings">
          {config.apps.map((app, index) => {
            const base = `${kp}.apps.${app.key}`;
            return (
              <li
                key={app.key}
                className={app.isBeancount ? "compare-highlight" : undefined}
              >
                <div className="compare-ranked-product">
                  <span className="compare-rank" aria-hidden="true">
                    {(index + 1).toLocaleString(locale)}
                  </span>
                  <div>
                    <h3>{t(appNameKey(app.key))}</h3>
                    <p>{rankingPrice(app, kp, locale, t, offersAsOf)}</p>
                  </div>
                </div>
                <div className="compare-ranking-details">
                  <p>{t(`${base}.tagline`)}</p>
                  <p>
                    <strong>{t("compareShared.roundup.bestForLabel")}</strong>{" "}
                    {t(`${base}.bestFor`, COMPARE_PRICE_PARAMS)}
                  </p>
                  <div className="compare-tradeoffs">
                    <div>
                      <h4>{t("compareShared.roundup.prosLabel")}</h4>
                      <ul>
                        {app.prosKeys.map((key) => (
                          <li key={key}>
                            {t(`${base}.pros.${key}`, COMPARE_PRICE_PARAMS)}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4>{t("compareShared.roundup.consLabel")}</h4>
                      <ul>
                        {app.consKeys.map((key) => (
                          <li key={key}>
                            {t(`${base}.cons.${key}`, COMPARE_PRICE_PARAMS)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {app.isBeancount ? (
                    <div className="compare-actions">
                      <ComparePrimaryActions config={config}>
                        <StartFreeLink surface="compare_ranking" />
                      </ComparePrimaryActions>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
